import * as React from 'react';

import {
  Badge,
  Button,
  DataGrid,
  type DataGridColumn,
  ListScreenLayout,
  Pagination,
  type PaginationProps,
  SearchField,
  Select,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlus } from 'lucide-react';

import {
  type CycleCountTaskRecord,
  type CycleCountTaskSortOption,
} from '../../_graphql/queryCycleCountTasks';
import * as state from '../../_state/cycleCountTasksList.state';
import { $remoteFacilities } from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';

import { CountTaskStatusBadge } from './CountTaskStatusBadge';
import { CreateCycleCountTaskDialog } from './CreateCycleCountTaskDialog';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: CycleCountTaskSortOption; desc: CycleCountTaskSortOption }
> = {
  taskNumber: { asc: 'TaskNumberAsc', desc: 'TaskNumberDesc' },
  status: { asc: 'StatusAsc', desc: 'StatusDesc' },
  countType: { asc: 'CountTypeAsc', desc: 'CountTypeDesc' },
  dueDate: { asc: 'DueDateAsc', desc: 'DueDateDesc' },
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '\u2014';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const CycleCountTasksListView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [selectedStatus, setSelectedStatus] = useAtom(state.$selectedStatus);
  const [selectedCountType, setSelectedCountType] = useAtom(
    state.$selectedCountType,
  );
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteCycleCountTasks,
  );

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const facilityMap = React.useMemo(() => {
    const map = new Map<string, string>();
    facilities.forEach((f) => map.set(f.id, f.name));
    return map;
  }, [facilities]);

  const tasks = React.useMemo(() => {
    let filtered = data?.tasks ?? [];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (task) =>
          task.taskNumber.toLowerCase().includes(term) ||
          (task.assignedToUserName &&
            task.assignedToUserName.toLowerCase().includes(term)),
      );
    }
    if (selectedCountType && selectedCountType !== 'all') {
      filtered = filtered.filter(
        (task) => task.countType === selectedCountType,
      );
    }
    return filtered;
  }, [data?.tasks, searchTerm, selectedCountType]);

  const total = data?.totalCount ?? 0;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting(['TaskNumberDesc']);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleRowClick = React.useCallback(
    (cell: { row: CycleCountTaskRecord }) => {
      setLocation(`/inventory/counts/tasks/${cell.row.id}`);
    },
    [setLocation],
  );

  const facilityOptions = React.useMemo(
    () => [
      { key: 'all', label: t.cycleCountTasksView.filters.allFacilities },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ],
    [facilities, t],
  );

  const statusOptions = React.useMemo(
    () => [
      { key: 'all', label: t.cycleCountTasksView.filters.allStatuses },
      {
        key: 'Pending',
        label: t.cycleCountTasksView.statusTypes.Pending,
      },
      {
        key: 'InProgress',
        label: t.cycleCountTasksView.statusTypes.InProgress,
      },
      {
        key: 'Completed',
        label: t.cycleCountTasksView.statusTypes.Completed,
      },
      {
        key: 'Approved',
        label: t.cycleCountTasksView.statusTypes.Approved,
      },
      {
        key: 'Cancelled',
        label: t.cycleCountTasksView.statusTypes.Cancelled,
      },
    ],
    [t],
  );

  const countTypeOptions = React.useMemo(
    () => [
      { key: 'all', label: t.cycleCountTasksView.filters.allCountTypes },
      {
        key: 'CycleCount',
        label: t.cycleCountTasksView.countTypes.CycleCount,
      },
      {
        key: 'PhysicalInventory',
        label: t.cycleCountTasksView.countTypes.PhysicalInventory,
      },
      { key: 'SpotCheck', label: t.cycleCountTasksView.countTypes.SpotCheck },
    ],
    [t],
  );

  const renderTaskNumberCell = React.useCallback(
    ({ row }: { row: CycleCountTaskRecord }) => (
      <span className="font-medium">{row.taskNumber}</span>
    ),
    [],
  );

  const renderFacilityCell = React.useCallback(
    ({ row }: { row: CycleCountTaskRecord }) => (
      <span className="text-sm">
        {facilityMap.get(row.facilityId) || row.facilityId}
      </span>
    ),
    [facilityMap],
  );

  const renderCountTypeCell = React.useCallback(
    ({ row }: { row: CycleCountTaskRecord }) => (
      <Badge color="neutral">
        {t.cycleCountTasksView.countTypes[row.countType] ?? row.countType}
      </Badge>
    ),
    [t],
  );

  const renderStatusCell = React.useCallback(
    ({ row }: { row: CycleCountTaskRecord }) => (
      <CountTaskStatusBadge status={row.status} />
    ),
    [],
  );

  const renderAssignedToCell = React.useCallback(
    ({ row }: { row: CycleCountTaskRecord }) => (
      <span className="text-sm">
        {row.assignedToUserName || (
          <span className="text-neutral/40">{'\u2014'}</span>
        )}
      </span>
    ),
    [],
  );

  const renderDueDateCell = React.useCallback(
    ({ row }: { row: CycleCountTaskRecord }) => (
      <span className="text-sm">{formatDate(row.dueDate)}</span>
    ),
    [],
  );

  const columns = React.useMemo(
    (): DataGridColumn<CycleCountTaskRecord>[] => [
      {
        key: 'taskNumber',
        header: t.cycleCountTasksView.table.columns.taskNumber,
        onClick: handleRowClick,
        cell: renderTaskNumberCell,
        width: 160,
        sortable: true,
      },
      {
        key: 'facility',
        header: t.cycleCountTasksView.table.columns.facility,
        onClick: handleRowClick,
        cell: renderFacilityCell,
        width: 180,
      },
      {
        key: 'countType',
        header: t.cycleCountTasksView.table.columns.countType,
        onClick: handleRowClick,
        cell: renderCountTypeCell,
        width: 160,
        sortable: true,
      },
      {
        key: 'status',
        header: t.cycleCountTasksView.table.columns.status,
        onClick: handleRowClick,
        cell: renderStatusCell,
        width: 130,
        sortable: true,
      },
      {
        key: 'assignedTo',
        header: t.cycleCountTasksView.table.columns.assignedTo,
        onClick: handleRowClick,
        cell: renderAssignedToCell,
        width: 160,
      },
      {
        key: 'dueDate',
        header: t.cycleCountTasksView.table.columns.dueDate,
        onClick: handleRowClick,
        cell: renderDueDateCell,
        width: 130,
        sortable: true,
      },
    ],
    [
      t,
      handleRowClick,
      renderTaskNumberCell,
      renderFacilityCell,
      renderCountTypeCell,
      renderStatusCell,
      renderAssignedToCell,
      renderDueDateCell,
    ],
  );

  return (
    <div className="@CycleCountTasksListView flex h-full flex-col">
      <CreateCycleCountTaskDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch()}
      />
      <ListScreenLayout
        actions={
          <Button
            icon={<LucidePlus />}
            color="primary"
            onPress={() => setIsCreateDialogOpen(true)}
          >
            {t.cycleCountTasksView.createTask.button}
          </Button>
        }
        filters={
          <>
            <SearchField
              autoFocus
              value={searchTerm ?? ''}
              onChange={(value) => {
                setSearchTerm(value || undefined);
                setOffset(0);
              }}
              placeholder={t.cycleCountTasksView.filters.search}
              className="w-64"
            />
            <Select
              value={selectedFacility || 'all'}
              onChange={(key) => {
                setSelectedFacility(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={facilityOptions}
              className="w-48"
            />
            <Select
              value={selectedStatus || 'all'}
              onChange={(key) => {
                setSelectedStatus(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={statusOptions}
              className="w-40"
            />
            <Select
              value={selectedCountType || 'all'}
              onChange={(key) => {
                setSelectedCountType(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={countTypeOptions}
              className="w-48"
            />
          </>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<CycleCountTaskRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          aria-label={t.cycleCountTasksView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.cycleCountTasksView.content.error(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={{
            title: t.cycleCountTasksView.emptyState.title,
            description: (
              <span>{t.cycleCountTasksView.emptyState.subtitle}</span>
            ),
            centered: true,
          }}
        />
      </ListScreenLayout>
    </div>
  );
};
